import React, {
  useEffect,
  useRef,
  useState,
  useMemo,
  useCallback,
} from "react";
import { useLocation } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  filterBy,
  CompositeFilterDescriptor,
} from "@progress/kendo-data-query";
import {
  GridPageChangeEvent,
  GridSelectionChangeEvent,
  GridHeaderSelectionChangeEvent,
  GridFilterChangeEvent,
} from "@progress/kendo-react-grid";
import { getter } from "@progress/kendo-react-common";
import { getSelectedState } from "@progress/kendo-react-data-tools";
import { Form, FormElement, FormRenderProps } from "@progress/kendo-react-form";

import { getAllActiveStates } from "../state/services/state.services";
import {
  findContactsByState,
  sendWhatsappBulkMessage,
  getWhatsAppTemplateApi,
} from "./services/bulkWhatsapp.services";
import { clearBulkWhatsappState } from "./bulkWhatsappSlice";
import { ISendWhatsappBulkMessagePayload } from "./bulkWhatsappModel";
import { LoadingPanel } from "../../components/layout/Loading";
import { ErrorToast } from "../../components/toast/Toasts";
import { checkAcessRights } from "../../_helper/helper";
import { INSERT_ACCESS } from "../../_contstants/common";

import { BulkWhatsappHeader } from "./components/BulkWhatsappHeader";
import { ContactSelectionSection } from "./components/ContactSelectionSection";
import { WhatsappComposerCard } from "./components/WhatsappComposerCard";
import { WhatsappLivePreview } from "./components/WhatsappLivePreview";

const DATA_ITEM_KEY = "ID";
const idGetter = getter(DATA_ITEM_KEY);

const options = [
  { label: "[name]", value: "[name]" },
  { label: "[mobileNumber]", value: "[mobileNumber]" },
  { label: "[emailID]", value: "[emailID]" },
  { label: "[referalCode]", value: "[referalCode]" },
  { label: "[cityName]", value: "[cityName]" },
];

const BulkWhatsapp: React.FC = () => {
  const location = useLocation();
  const dispatch = useAppDispatch();
  const gridRef = useRef<any>(null);

  const isSendAccess = Boolean(
    checkAcessRights(location.pathname, INSERT_ACCESS),
  );

  // Redux State
  const StateList = useAppSelector((state) => state.state?.StateList || []);
  const ContactList = useAppSelector((state) => state.bulkWhatsapp.ContactList);
  const apiTemplates = useAppSelector(
    (state) => state.bulkWhatsapp.apiTemplates,
  );
  const loading = useAppSelector((state) => state.bulkWhatsapp.loading);
  const sending = useAppSelector((state) => state.bulkWhatsapp.sending);

  // Local selection states
  const [selectedStateID, setSelectedStateID] = useState<number | null>(null);
  const [selectedApiTemplateID, setSelectedApiTemplateID] = useState<
    any | null
  >(null);
  const [selectedGridRows, setSelectedGridRows] = useState<{
    [key: string]: boolean | number[];
  }>({});
  const [filter, setFilter] = useState<CompositeFilterDescriptor | undefined>(
    undefined,
  );

  // Pagination states
  const [page, setPage] = useState({ skip: 0, take: 10 });

  useEffect(() => {
    dispatch(getAllActiveStates());
    dispatch(getWhatsAppTemplateApi());
    dispatch(findContactsByState({ StateID: 0 }));
    return () => {
      dispatch(clearBulkWhatsappState());
    };
  }, [dispatch]);

  // Reset selection when ContactList changes
  useEffect(() => {
    setSelectedGridRows({});
    setFilter(undefined);
    setPage({ skip: 0, take: 10 });
  }, [ContactList]);

  // Computed FullName
  const dataWithFullName = useMemo(() => {
    if (!ContactList) return [];
    return ContactList.map((item) => ({
      ...item,
      FullName: `${item.FirstName || ""} ${item.LastName || ""}`.trim(),
    }));
  }, [ContactList]);

  // Filtered contacts list
  const filteredContactList = useMemo(() => {
    if (!dataWithFullName) return [];
    if (!filter) return dataWithFullName;
    return filterBy(dataWithFullName, filter);
  }, [dataWithFullName, filter]);

  // Paged contacts list
  const pagedContactList = useMemo(() => {
    return filteredContactList.slice(page.skip, page.skip + page.take);
  }, [filteredContactList, page]);

  // Options
  const stateOptions = useMemo(
    () =>
      StateList?.map((s: any) => ({
        value: s.ID,
        label: s.StateName,
      })) || [],
    [StateList],
  );

  const apiTemplateOptions = useMemo(() => {
    const rawTemplates: any = apiTemplates;
    if (!rawTemplates) return [];
    const list: any[] = rawTemplates?.Body?.results?.templates || [];
    return list.map((t: any, index: number) => ({
      value: t.id || t.wa_template_id || t.name || index,
      label: t.display_name || t.name || `API Template ${index + 1}`,
      raw: t,
    }));
  }, [apiTemplates]);

  const selectedApiTemplate = useMemo(() => {
    if (
      selectedApiTemplateID === null ||
      selectedApiTemplateID === undefined ||
      !apiTemplates
    )
      return null;

    const targetVal =
      typeof selectedApiTemplateID === "object" &&
      selectedApiTemplateID !== null
        ? (selectedApiTemplateID.value ??
          selectedApiTemplateID.id ??
          selectedApiTemplateID.name)
        : selectedApiTemplateID;

    const rawTemplates: any = apiTemplates;
    const list: any[] = rawTemplates?.Body?.results?.templates || [];
    return (
      list.find(
        (t: any, index: number) =>
          t.id === targetVal ||
          t.wa_template_id === targetVal ||
          t.name === targetVal ||
          String(t.id) === String(targetVal) ||
          String(t.wa_template_id) === String(targetVal) ||
          String(t.name) === String(targetVal) ||
          index === targetVal,
      ) || null
    );
  }, [selectedApiTemplateID, apiTemplates]);

  const parsedApiButtons = useMemo(() => {
    if (!selectedApiTemplate?.buttons) return [];
    try {
      let raw = selectedApiTemplate.buttons;
      if (typeof raw === "string") {
        if (raw.startsWith('"') && raw.endsWith('"')) {
          raw = JSON.parse(raw);
        }
        if (typeof raw === "string") {
          raw = JSON.parse(raw);
        }
      }
      return Array.isArray(raw) ? raw : [];
    } catch (e) {
      return [];
    }
  }, [selectedApiTemplate]);

  const activeVariables = useMemo(() => {
    const body = selectedApiTemplate?.body || "";
    if (!body) return [];
    const regex = /{{(\d+)}}/g;
    const matches: number[] = [];
    let match;
    while ((match = regex.exec(body)) !== null) {
      const num = Number(match[1]);
      if (!matches.includes(num)) {
        matches.push(num);
      }
    }
    return matches;
  }, [selectedApiTemplate]);

  // Handlers
  const handleStateChange = (event: any) => {
    const val = event?.value ?? null;
    const targetVal =
      typeof val === "object" && val !== null ? (val.value ?? val.ID ?? null) : val;

    setSelectedStateID(targetVal);
    setSelectedGridRows({});
    setFilter(undefined);
    setPage({ skip: 0, take: 10 });
    if (targetVal) {
      dispatch(findContactsByState({ StateID: targetVal }));
    } else {
      dispatch(findContactsByState({ StateID: 0 }));
    }
  };

  const handleFilterChange = (e: GridFilterChangeEvent) => {
    setFilter(e.filter);
    setPage({ skip: 0, take: page.take });
  };

  const handlePageChange = (event: GridPageChangeEvent) => {
    setPage(event.page);
  };

  const handleApiTemplateChange = (
    event: any,
    formRenderProps: FormRenderProps,
  ) => {
    const selectedItem = event?.value ?? event?.target?.value ?? event;
    let targetId = selectedItem;
    if (typeof selectedItem === "object" && selectedItem !== null) {
      targetId =
        selectedItem.value ??
        selectedItem.id ??
        selectedItem.wa_template_id ??
        selectedItem.name ??
        null;
    }
    setSelectedApiTemplateID(targetId);
    if (formRenderProps) {
      formRenderProps.onChange("ApiTemplateID", { value: selectedItem });
    }
  };

  const onSelectionChange = useCallback(
    (event: GridSelectionChangeEvent) => {
      const newSelectedState = getSelectedState({
        event,
        selectedState: selectedGridRows,
        dataItemKey: DATA_ITEM_KEY,
      });
      setSelectedGridRows(newSelectedState);
    },
    [selectedGridRows],
  );

  const onHeaderSelectionChange = useCallback(
    (event: GridHeaderSelectionChangeEvent) => {
      const checkboxElement: any = event.syntheticEvent.target;
      const checked = checkboxElement.checked;
      const newSelectedState: { [key: string]: boolean | number[] } = {
        ...selectedGridRows,
      };

      const currentPageData = filteredContactList.slice(
        page.skip,
        page.skip + page.take,
      );
      currentPageData.forEach((item: any) => {
        newSelectedState[idGetter(item)] = checked;
      });
      setSelectedGridRows(newSelectedState);
    },
    [filteredContactList, page, selectedGridRows],
  );

  const selectedCount = useMemo(() => {
    return (ContactList || []).filter(
      (item) => selectedGridRows[idGetter(item)] === true,
    ).length;
  }, [ContactList, selectedGridRows]);

  const getFormValues = (renderProps: FormRenderProps) => {
    const values: any = {
      StateID: renderProps.valueGetter("StateID"),
      ApiTemplateID: renderProps.valueGetter("ApiTemplateID"),
    };
    activeVariables.forEach((v) => {
      values[`Variable${v}`] = renderProps.valueGetter(`Variable${v}`);
    });
    return values;
  };

  const handleSendWhatsapp = async (formValues: any) => {
    if (!isSendAccess) {
      ErrorToast("You do not have rights to send WhatsApp messages!");
      return;
    }
    if (!selectedApiTemplate) {
      ErrorToast("Please select a WhatsApp Template!");
      return;
    }

    const selectedContacts = (ContactList || [])
      .filter((item) => selectedGridRows[idGetter(item)] === true)
      .filter((item) => item.MobileNo && String(item.MobileNo).trim() !== "");

    if (selectedContacts.length === 0) {
      ErrorToast(
        "Please select at least one contact with a valid mobile number!",
      );
      return;
    }

    if (activeVariables.length > 0) {
      const missingVars = activeVariables.filter((varNum) => {
        const val = formValues[`Variable${varNum}`];
        if (!val) return true;
        if (typeof val === "object" && val !== null) {
          return (
            !val.value &&
            !val.label &&
            (typeof val.value !== "string" || val.value.trim() === "")
          );
        }
        return String(val).trim() === "";
      });

      if (missingVars.length > 0) {
        ErrorToast(
          missingVars.length === 1
            ? `Please select a value for Variable ${missingVars[0]}`
            : `Please select values for variables: ${missingVars.join(", ")}`,
        );
        return;
      }
    }

    const contactNoList = selectedContacts.map((contact) => {
      const variables: string[] = [];
      for (const varNum of activeVariables) {
        const selectedVariable = formValues[`Variable${varNum}`];
        let rawVal = "";
        if (typeof selectedVariable === "object" && selectedVariable !== null) {
          rawVal = String(
            selectedVariable.value ?? selectedVariable.label ?? "",
          );
        } else if (
          selectedVariable !== undefined &&
          selectedVariable !== null
        ) {
          rawVal = String(selectedVariable);
        }
        let resolvedVal = "";
        const lower = String(rawVal).toLowerCase().trim();
        if (lower === "[name]") {
          resolvedVal =
            `${contact.FirstName || ""} ${contact.LastName || ""}`.trim();
        } else if (lower === "[mobilenumber]") {
          resolvedVal = String(contact.MobileNo || "");
        } else if (lower === "[emailid]") {
          resolvedVal = String(contact.EmailID || "");
        } else if (lower === "[cityname]") {
          resolvedVal = String(contact.CityName || "");
        } else if (lower === "[referalcode]") {
          resolvedVal = String(contact.ReferalCode || "");
        } else {
          resolvedVal = String(rawVal);
        }
        variables.push(String(resolvedVal));
      }
      return {
        MobileNo: String(contact.MobileNo || ""),
        Variables: variables,
      };
    });

    const templateName =
      selectedApiTemplate?.name ||
      selectedApiTemplate?.wa_template_name ||
      selectedApiTemplate?.template_name ||
      (typeof selectedApiTemplateID === "string"
        ? selectedApiTemplateID
        : "") ||
      "";

    if (!templateName) {
      ErrorToast("Selected WhatsApp Template name is invalid or missing!");
      return;
    }

    const payload: ISendWhatsappBulkMessagePayload = {
      TemplateName: templateName,
      TemplateLanguage: selectedApiTemplate?.language || "en",
      ContactNo: contactNoList,
    };

    if (selectedApiTemplate?.header_handle_file_url) {
      payload.HeaderValue = selectedApiTemplate.header_handle_file_url;
    }
    if (selectedApiTemplate?.header_handle_file_name) {
      payload.fileName = selectedApiTemplate.header_handle_file_name;
    }

    const response = await dispatch(sendWhatsappBulkMessage(payload));
    if (response?.meta?.requestStatus === "fulfilled") {
      setSelectedGridRows({});
    }
  };

  const renderPreviewWithVariables = (
    text: string,
    formRenderProps: FormRenderProps,
  ) => {
    if (!text) return "";
    return text.replace(/{{(\d+)}}/g, (match: string, p1: string) => {
      const val = formRenderProps.valueGetter(`Variable${p1}`);
      const displayVal =
        typeof val === "object" && val !== null
          ? String(val.label ?? val.value ?? "")
          : typeof val === "string"
            ? val
            : val !== undefined && val !== null
              ? String(val)
              : "";
      return displayVal && displayVal.trim() !== "" ? displayVal : match;
    });
  };

  return (
    <div style={{ boxSizing: "border-box" }}>
      {(loading || sending) && <LoadingPanel gridRef={gridRef} />}

      <Form
        render={(formRenderProps) => (
          <FormElement>
            {/* Header */}
            <BulkWhatsappHeader
              selectedCount={selectedCount}
              isSendAccess={isSendAccess}
            />

            {/* 2-Column Responsive Workspace */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "3fr minmax(350px, 1fr)",
                gap: "20px",
                alignItems: "start",
              }}
            >
              {/* Left Column: Contact Grid */}
              <ContactSelectionSection
                contactList={ContactList || []}
                filteredContactList={filteredContactList}
                pagedContactList={pagedContactList}
                selectedGridRows={selectedGridRows}
                filter={filter}
                page={page}
                onFilterChange={handleFilterChange}
                onPageChange={handlePageChange}
                onSelectionChange={onSelectionChange}
                onHeaderSelectionChange={onHeaderSelectionChange}
                stateOptions={stateOptions}
                selectedStateID={selectedStateID}
                onStateChange={handleStateChange}
              />

              {/* Right Column: WhatsApp Configuration Form */}
              <WhatsappComposerCard
                apiTemplateOptions={apiTemplateOptions}
                selectedApiTemplate={selectedApiTemplate}
                activeVariables={activeVariables}
                selectedCount={selectedCount}
                sending={sending}
                isSendAccess={isSendAccess}
                options={options}
                onApiTemplateChange={handleApiTemplateChange}
                onSubmit={handleSendWhatsapp}
                getFormValues={getFormValues}
                formRenderProps={formRenderProps}
              />
            </div>

            {/* Floating Live WhatsApp Preview Widget */}
            <WhatsappLivePreview
              selectedApiTemplate={selectedApiTemplate}
              parsedApiButtons={parsedApiButtons}
              selectedCount={selectedCount}
              formRenderProps={formRenderProps}
              renderPreviewWithVariables={renderPreviewWithVariables}
              onSendClick={() => {
                const values = getFormValues(formRenderProps);
                handleSendWhatsapp(values);
              }}
              sending={sending}
              isSendAccess={isSendAccess}
              isFloating={true}
            />
          </FormElement>
        )}
      />
    </div>
  );
};

export default BulkWhatsapp;
